import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Building2, Briefcase, Pencil, AlertTriangle, Clock, FileWarning, Plus } from "lucide-react";
import { flagEmoji } from "@/lib/countries";
import ClientDocumentsPanel from "@/components/ClientDocumentsPanel";
import { useAuth } from "@/lib/auth";
import Flag from "@/components/Flag";
import ClientContactsManager from "@/components/ClientContactsManager";
import IconAction from "@/components/IconAction";
import ForfaitProgress from "@/components/ForfaitProgress";

const statusColor = {
  DRAFT: "bg-zinc-100 text-zinc-700",
  ACTIVE: "bg-emerald-100 text-emerald-800",
  CLOSED: "bg-zinc-200 text-zinc-600",
  ARCHIVED: "bg-zinc-200 text-zinc-500",
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isCEO = user?.role === "CEO";
  const canCreateMission = ["CEO", "MANAGER", "GESTIONNAIRE"].includes(user?.role);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/missions?client_id=${encodeURIComponent(id)}`),
    ]).then(([c, m]) => {
      if (!active) return;
      setClient(c.data);
      setMissions(m.data);
    }).catch((e) => {
      if (e?.response?.status === 404) navigate("/clients");
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id, navigate]);

  if (loading) return <div className="p-12 text-zinc-500">Loading…</div>;
  if (!client) return null;

  const totalBudget = missions.reduce((acc, m) => acc + (m.budget_jh || 0), 0);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1300px]" data-testid="client-detail-page">
      <Link to="/clients" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900 mb-6" data-testid="client-back-btn">
        <ArrowLeft size={13} /> Back to clients
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div className="flex items-start sm:items-center gap-4 min-w-0">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-md bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0">
            <Building2 size={24} />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2 font-mono">Client</div>
            <h1 className="font-heading text-xl sm:text-2xl md:text-4xl font-bold tracking-tight flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="client-detail-name">
              <span className="break-words">{client.name}</span>
              {client.country && (
                <span data-testid="client-detail-country-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-mono uppercase bg-zinc-100 text-zinc-700 border border-zinc-200">
                  <Flag code={client.country} size={14} alt={false} />{client.country}
                </span>
              )}
              {client.purchase_order_only && (
                <span data-testid="client-detail-po-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-mono uppercase bg-blue-100 text-blue-900 border border-blue-200" title="Client facturé sur bon de commande uniquement">
                  Avec bon de commande
                </span>
              )}
            </h1>
            {client.group_name && <p className="text-zinc-500 mt-1 text-sm truncate">{client.group_name}</p>}
          </div>
        </div>
        {/* Iter-91: Edit button → routes to Clients page with ?edit=<id> so the existing
            Clients.jsx dialog opens pre-filled on this client. Avoids duplicating the form. */}
        <IconAction icon={Pencil} size="lg" tone="dark" testid="client-detail-edit-btn" label="Éditer le client" onClick={() => navigate(`/clients?edit=${client.client_id}`)} />
      </div>

      {/* Meta cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        <MetaCard label="ICE" value={client.ice || "—"} mono />
        <MetaCard label="Currency" value={client.currency || "MAD"} mono />
        <MetaCard label="Billing type" value={client.billing_type === "TVA_20" ? "TTC (VAT 20%)" : "HT (no VAT)"} />
        <MetaCard label="Missions · Σ JH" value={`${missions.length} · ${totalBudget.toLocaleString()} JH`} />
      </div>

      {client.address && (
        <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">Address</div>
          <div className="text-sm text-zinc-800 whitespace-pre-line">{client.address}</div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-8" data-testid="client-detail-contacts">
        <ClientContactsManager clientId={client.client_id} />
      </div>

      {/* Iter-94: status banners */}
      {client.status === "PENDING_VALIDATION" && (
        <div data-testid="client-banner-pending" className="flex flex-col sm:flex-row sm:items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <Clock size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-900">Client en attente de validation CEO</div>
            <p className="text-xs text-amber-800 mt-1">
              Ce client a été créé par un manager ou un gestionnaire. Le CEO doit lui attribuer un dossier Drive pour finaliser la validation.
              Les missions peuvent être créées dès maintenant, mais porteront le badge <em>« Contrat client en attente »</em> jusqu'au dépôt d'un contrat cadre.
            </p>
          </div>
          {isCEO && (
            <div className="flex flex-col gap-2 w-full sm:w-auto shrink-0">
              <button
                onClick={() => navigate(`/clients?edit=${client.client_id}`)}
                data-testid="client-banner-validate-btn"
                className="w-full sm:w-auto px-3 h-9 bg-amber-600 text-white text-xs rounded-md hover:bg-amber-700 whitespace-nowrap"
              >
                Valider via Drive
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Marquer ce client comme ACTIVE sans Drive folder ?\n\nUtile pour rattraper un client existant bloqué en PENDING_VALIDATION (legacy).")) return;
                  try {
                    const r = await api.post(`/clients/${client.client_id}/validate-now`);
                    toast.success("Client validé");
                    setClient(r.data);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Échec validation");
                  }
                }}
                data-testid="client-banner-validate-now-btn"
                className="w-full sm:w-auto px-3 h-9 border border-amber-300 bg-white text-amber-900 text-xs rounded-md hover:bg-amber-50 whitespace-nowrap"
              >
                Valider sans Drive
              </button>
            </div>
          )}
        </div>
      )}
      {client.status !== "PENDING_VALIDATION" && !client.has_contract && !client.purchase_order_only && (
        <div data-testid="client-banner-no-contract" className="flex flex-col sm:flex-row sm:items-start gap-3 bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <FileWarning size={18} className="text-orange-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-orange-900">Contrat cadre client en attente</div>
            <p className="text-xs text-orange-800 mt-1">
              Aucun contrat cadre n'a encore été déposé pour ce client. Les missions associées porteront ce statut tant qu'un document de type <strong>« Contrat cadre »</strong> n'aura pas été uploadé ci-dessous.
            </p>
          </div>
        </div>
      )}
      {isCEO && !client.has_drive_folder && client.status !== "PENDING_VALIDATION" && (
        <div data-testid="client-banner-no-drive" className="flex flex-col sm:flex-row sm:items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <AlertTriangle size={18} className="text-red-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-red-900">Dossier Drive manquant</div>
            <p className="text-xs text-red-800 mt-1">
              Aucun dossier Drive n'est attribué à ce client. Les documents uploadés resteront stockés localement jusqu'à ce qu'un dossier soit configuré.
            </p>
          </div>
          <button
            onClick={() => navigate(`/clients?edit=${client.client_id}`)}
            className="w-full sm:w-auto px-3 h-9 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 whitespace-nowrap shrink-0"
          >
            Configurer
          </button>
        </div>
      )}

      {/* Iter-103: PO-only flag — toggle for clients without master contract */}
      {(["CEO", "ADMIN", "GESTIONNAIRE"].includes(user?.role)) && (
        <div data-testid="client-po-only-toggle" className="mb-4 flex items-start gap-3 bg-white border border-zinc-200 rounded-lg p-4">
          <input
            type="checkbox"
            id="po-only"
            data-testid="client-po-only-checkbox"
            checked={!!client.purchase_order_only}
            onChange={async (e) => {
              const next = e.target.checked;
              try {
                await api.patch(`/clients/${client.client_id}`, {
                  name: client.name,
                  country: client.country,
                  purchase_order_only: next,
                });
                setClient({ ...client, purchase_order_only: next });
              } catch {
                // toast handled by api interceptor
              }
            }}
            className="mt-0.5 w-4 h-4 accent-zinc-900"
          />
          <label htmlFor="po-only" className="flex-1 cursor-pointer">
            <div className="text-sm font-medium text-brand-navy">Ce client travaille sur bon de commande uniquement</div>
            <div className="text-xs text-zinc-600 mt-0.5">
              Cochez si ce client n'a pas de contrat cadre et opère par bons de commande successifs.
              L'alerte « contrat cadre en attente » sera désactivée et un tag <strong>« Avec bon de commande »</strong> remplacera <strong>« No contract »</strong> sur les listes.
            </div>
          </label>
        </div>
      )}

      {/* Iter-92 Phase B — Administrative documents gallery (contracts, NDAs, …) */}
      <div className="mb-8">
        <ClientDocumentsPanel clientId={client.client_id} />
      </div>

      {/* Missions */}
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2"><Briefcase size={20} /> Missions</h2>
        <div className="flex items-center gap-4">
          <div className="text-xs text-zinc-500 font-mono">{missions.length} mission(s)</div>
          {canCreateMission && (
            <Link
              to={`/missions/new?client_id=${encodeURIComponent(id)}`}
              data-testid="client-new-mission-btn"
              className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600"
            >
              <Plus size={14} /> New mission
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto md:overflow-hidden">
        <div className="grid grid-cols-12 min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Budget</div>
          <div className="col-span-2">Dates</div>
          <div className="col-span-1 text-right">Open</div>
        </div>
        {missions.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center" data-testid="client-no-missions">
            <div>No missions for this client yet.</div>
            {canCreateMission && (
              <Link
                to={`/missions/new?client_id=${encodeURIComponent(id)}`}
                data-testid="client-no-missions-cta"
                className="inline-flex items-center gap-1.5 mt-4 px-4 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600"
              >
                <Plus size={14} /> Create the first mission
              </Link>
            )}
          </div>
        )}
        {missions.map((m) => (
          <Link key={m.mission_id} to={`/missions/${m.mission_id}`} data-testid={`client-mission-${m.mission_id}`}
                className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-5 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors">
            <div className="col-span-5 flex items-center gap-3 min-w-0">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: m.color || "#64748B" }} />
              <div className="min-w-0">
                <div className="font-medium truncate">{m.title}</div>
                {m.description && <div className="text-[11px] text-zinc-500 truncate">{m.description}</div>}
                {m.type === "FORFAIT" && <ForfaitProgress mission={m} />}
              </div>
            </div>
            <div className="col-span-2"><span className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase ${statusColor[m.status] || "bg-zinc-100"}`}>{m.status}</span></div>
            <div className="col-span-2 text-xs font-mono text-zinc-700">{m.type === "FORFAIT" ? <span className="text-zinc-400">—</span> : `${m.budget_jh} JH`}</div>
            <div className="col-span-2 text-xs font-mono text-zinc-500">{m.start_date}</div>
            <div className="col-span-1 text-right text-xs text-zinc-400">→</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function MetaCard({ label, value, mono }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-zinc-900 truncate`}>{value}</div>
    </div>
  );
}
